import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../../dashboard.style.js';
import { Firestore } from 'firebase/firestore';
import { getSafetyReportsByCompany, getActionsByCompany, getUsersByCompany } from '@cmms/api';
import { SafetyReportType, SafetyActionStatus, SafetyReportStatus } from '@cmms/models';

@customElement('safety-mini-dashboard')
export class SafetyMiniDashboard extends LitElement {
  @property({ type: String }) companyId = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  // Derived KPI state
  @state() private totalReports = 0;
  @state() private openReports = 0;
  @state() private resolvedReports = 0;
  @state() private totalActions = 0;
  @state() private completedActions = 0;
  @state() private openActions = 0;
  @state() private overdueActions = 0;
  @state() private complianceRate = 0;
  @state() private reportsWithActions = 0;

  // Aggregations and recents
  @state() private incidentsThisMonth = 0;
  @state() private incidentsByType: Array<{ type: string; count: number }> = [];
  @state() private actionsByStatus: Array<{ status: string; count: number }> = [];
  @state() private recentIncidents: Array<{ title: string; date: number; status: string; assigned_to?: string }> = [];
  @state() private recentActions: Array<{ title: string; due: number; status: string; assigned_to?: string }> = [];
  @state() private users: Record<string, string> = {};

  static styles = [
    styles,
    css`
      .mini-dashboard { display:flex; flex-direction:column; gap:1.5rem; background: var(--sl-color-neutral-50); padding:1rem; }
      .mini-header { display:flex; align-items:center; gap:1rem; }
      .bar-list { display:flex; flex-direction:column; gap:0.5rem; }
      .bar-row { display:flex; align-items:center; gap:0.75rem; }
      .bar { flex:1; height:10px; background: var(--sl-color-neutral-200); border-radius:999px; overflow:hidden; }
      .bar > div { height:100%; background: var(--sl-color-primary-500); }
      .label { min-width: 160px; color: var(--sl-color-neutral-700); }
      .value { width: 40px; text-align:right; color: var(--sl-color-neutral-800); font-variant-numeric: tabular-nums; }
      .data-table { width:100%; border-collapse:collapse; }
      .data-table th, .data-table td { padding:0.75rem; text-align:left; border-bottom:1px solid var(--sl-color-neutral-200); }
      .data-table th { color: var(--sl-color-neutral-600); font-weight:600; font-size:0.875rem; }
      .data-table td { color: var(--sl-color-neutral-900); font-size:0.875rem; }
    `
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this.loadData();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('companyId')) this.loadData();
  }

  private formatDate(ts?: number) { return ts ? new Date(ts).toLocaleDateString() : ''; }

  private badgeVariant(status: string) {
    switch (status) {
      case SafetyReportStatus.OPEN: return 'warning';
      case SafetyReportStatus.ASSIGNED: return 'primary';
      case SafetyReportStatus.INVESTIGATING: return 'danger';
      case SafetyReportStatus.RESOLVED: return 'success';
      default: return 'neutral';
    }
  }

  private async loadData() {
    if (!this.companyId || !this.firestore) return;
    try {
      const [reportsResp, actionsResp, usersResp] = await Promise.all([
        getSafetyReportsByCompany(this.firestore, this.companyId),
        getActionsByCompany(this.firestore, this.companyId),
        getUsersByCompany(this.firestore, this.companyId)
      ]);
      const reportsRaw: any[] = Array.isArray((reportsResp as any).data) ? (reportsResp as any).data : (Array.isArray(reportsResp) ? (reportsResp as any) : []);
      const actionsRaw: any[] = Array.isArray((actionsResp as any).data) ? (actionsResp as any).data : (Array.isArray(actionsResp) ? (actionsResp as any) : []);
      const rawUsers: any[] = Array.isArray((usersResp as any).data) ? (usersResp as any).data : (Array.isArray(usersResp) ? (usersResp as any) : []);

      this.users = {};
      for (const u of rawUsers) {
        const id = u.id;
        const m = u.model || {};
        this.users[id] = m.full_name || m.name || u.name || 'User';
      }

      // Normalize via toJSON
      const reports = (reportsRaw || []).map(r => ({ id: r.id, ...(r.model as any).toJSON?.() ?? (r.model || r) }));
      const actions = (actionsRaw || []).map(a => ({ id: a.id, ...(a.model as any).toJSON?.() ?? (a.model || a) }));

      // Derived KPIs
      this.totalReports = reports.length;
      this.openReports = reports.filter(r => (r.status || '').toLowerCase() !== SafetyReportStatus.RESOLVED).length;
      this.resolvedReports = this.totalReports - this.openReports;

      this.totalActions = actions.length;
      this.completedActions = actions.filter(a => a.status === SafetyActionStatus.COMPLETED).length;
      const openStatuses = new Set([SafetyActionStatus.TODO, SafetyActionStatus.IN_PROGRESS]);
      this.openActions = actions.filter(a => openStatuses.has(a.status)).length;
      this.overdueActions = actions.filter(a => a.due_date && a.status !== SafetyActionStatus.COMPLETED && a.due_date < Date.now()).length;
      this.complianceRate = this.totalActions ? +(100 * (this.completedActions / this.totalActions)).toFixed(1) : 0;

      const reportIdsWithActions = new Set(actions.filter(a => !!a.related_report_id).map(a => a.related_report_id));
      this.reportsWithActions = reports.filter(r => reportIdsWithActions.has(r.id)).length;

      // Incidents this month
      const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
      const rnorm = reports.map(r => ({
        title: r.title || 'Untitled',
        date_reported: r.date_reported || r.created_at || 0,
        report_type: r.report_type || 'unknown',
        status: r.status || 'open',
        assigned_to: r.assigned_to_user_id || r.assigned_to || ''
      }));
      this.incidentsThisMonth = rnorm.filter(r => r.date_reported >= startOfMonth).length;

      // Incidents by type
      const byType = new Map<string, number>();
      for (const r of rnorm) byType.set(r.report_type, (byType.get(r.report_type) || 0) + 1);
      this.incidentsByType = Array.from(byType.entries()).map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count);

      // Actions by status
      const statusMap = new Map<string, number>();
      for (const a of actions) statusMap.set(a.status || 'unknown', (statusMap.get(a.status || 'unknown') || 0) + 1);
      this.actionsByStatus = Array.from(statusMap.entries()).map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count);

      // Recent incidents (5)
      this.recentIncidents = rnorm
        .sort((a, b) => b.date_reported - a.date_reported)
        .slice(0, 5)
        .map(r => ({ title: r.title, date: r.date_reported, status: r.status, assigned_to: r.assigned_to }));

      // Recent actions (5 by due date asc)
      const anorm = actions.map(a => ({
        title: a.action_title || a.title || 'Untitled',
        due_date: a.due_date || 0,
        status: a.status || 'todo',
        assigned_to: a.assigned_to_user_id || a.assigned_to || ''
      }));
      this.recentActions = anorm
        .sort((a, b) => (a.due_date || 0) - (b.due_date || 0))
        .slice(0, 5)
        .map(a => ({ title: a.title, due: a.due_date, status: a.status, assigned_to: a.assigned_to }));

      this.requestUpdate();
    } catch (err) {
      console.error('Failed to load safety data:', err);
      this.totalReports = 0;
      this.openReports = 0;
      this.resolvedReports = 0;
      this.totalActions = 0;
      this.completedActions = 0;
      this.openActions = 0;
      this.overdueActions = 0;
      this.complianceRate = 0;
      this.reportsWithActions = 0;
      this.incidentsThisMonth = 0;
      this.incidentsByType = [];
      this.actionsByStatus = [];
      this.recentIncidents = [];
      this.recentActions = [];
    }
  }

  render() {
    return html`
      <div class="mini-dashboard">
        <div class="mini-header" style="display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem;">
          <h2 style="margin:0;font-size:1.25rem;">Health & Safety Overview</h2>
        </div>

        <!-- KPI cards -->
        <div class="overview-cards">
          <div class="overview-card"><h3>Open Reports</h3><p class="value">${this.openReports}</p></div>
          <div class="overview-card"><h3>Open Actions</h3><p class="value">${this.openActions}</p></div>
          <div class="overview-card"><h3>Overdue Actions</h3><p class="value">${this.overdueActions}</p></div>
          <div class="overview-card"><h3>Compliance (%)</h3><p class="value">${this.complianceRate.toFixed(1)}</p></div>
        </div>

        <div class="widget">
          <h3>Incidents by Type</h3>
          <div class="bar-list">
            ${(() => {
              const max = Math.max(1, ...this.incidentsByType.map(t => t.count));
              const label = (t: string) => {
                switch (t) {
                  case SafetyReportType.SAFETY_OBSERVATION: return 'Safety Observation';
                  case SafetyReportType.NEAR_MISS: return 'Near Miss';
                  case SafetyReportType.INCIDENT: return 'Incident';
                  default: return String(t);
                }
              };
              return this.incidentsByType.map(t => html`
                <div class="bar-row">
                  <div class="label">${label(t.type)}</div>
                  <div class="bar"><div style="width:${Math.round((t.count / max) * 100)}%"></div></div>
                  <div class="value">${t.count}</div>
                </div>
              `);
            })()}
          </div>
        </div>

        <div class="widget">
          <h3>Actions by Status</h3>
          <div class="bar-list">
            ${(() => {
              const max = Math.max(1, ...this.actionsByStatus.map(t => t.count));
              const label = (s: string) => {
                switch (s) {
                  case SafetyActionStatus.TODO: return 'To Do';
                  case SafetyActionStatus.IN_PROGRESS: return 'In Progress';
                  case SafetyActionStatus.COMPLETED: return 'Completed';
                  default: return String(s || 'Unknown');
                }
              };
              return this.actionsByStatus.map(s => html`
                <div class="bar-row">
                  <div class="label">${label(s.status)}</div>
                  <div class="bar"><div style="width:${Math.round((s.count / max) * 100)}%"></div></div>
                  <div class="value">${s.count}</div>
                </div>
              `);
            })()}
          </div>
        </div>

        <div class="widget">
          <h3>Recent Reports</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Title</th><th>Date</th><th>Status</th><th>Assigned To</th></tr></thead>
              <tbody>
                ${this.recentIncidents.map(i => html`
                  <tr>
                    <td>${i.title}</td>
                    <td>${this.formatDate(i.date)}</td>
                    <td><sl-badge variant=${this.badgeVariant(i.status)}>${(i.status || '').toString().toUpperCase()}</sl-badge></td>
                    <td>${this.users[i.assigned_to || ''] || i.assigned_to || '-'}</td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </div>

        <div class="widget">
          <h3>Recent Actions</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Title</th><th>Assigned To</th><th>Due Date</th><th>Status</th></tr></thead>
              <tbody>
                ${this.recentActions.map(a => html`
                  <tr>
                    <td>${a.title}</td>
                    <td>${this.users[a.assigned_to || ''] || a.assigned_to || '-'}</td>
                    <td>${this.formatDate(a.due)}</td>
                    <td><sl-badge variant=${this.badgeVariant(a.status)}>${(a.status || '').toString().toUpperCase()}</sl-badge></td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'safety-mini-dashboard': SafetyMiniDashboard;
  }
}
